import { FilterNamesEnum } from '@/client/helpers';
import { TableNamesEnum, TableNamesKeys } from '@/common/constants';
import { DataTableFilter } from '@/components/tables/crud/filters/DataTableFilter';
import { FilterType } from '@/components/tables/crud/types';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectOpenFilters, setOpenFilters } from '@/store/features/filters';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FilterTypeEnum } from '../types/filter.type';

type FiltersProps = {
  filters: FilterType[];
  onFilter: (filters: Record<string, any>) => void;
  tableName?: TableNamesEnum;
  defaultValues?: Record<string, any>;
  forcedFilter?: Record<string, any>;
  setOpenedFilters?: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  doNotUseInContainer?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const filtersReducer = (
  state: Record<string, { value: any; filter: FilterType }>,
  action: {
    type: 'updateValue' | 'clear' | 'remove' | 'add';
    key: string;
    value?: any;
    filter?: FilterType;
    defaultValues?: Record<string, { value: any; filter: FilterType }>;
  },
): Record<string, { value: any; filter: FilterType }> => {
  const newState: typeof state = {};

  switch (action.type) {
    case 'clear':
      return action.defaultValues || {};
    case 'remove':
      Object.keys(state)
        .filter((x) => x !== action.key)
        .map((x) => (newState[x] = state[x]));

      return { ...newState };
    case 'add':
      return {
        ...state,
        [action.key]: {
          value: action.filter?.defaultValue ?? null,
          filter: action.filter as FilterType,
        },
      };
    case 'updateValue':
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          value: action.value,
        },
      };
    default:
      return state;
  }
};

const buildFiltersFromValues = (
  filters: FilterType[],
  values: Record<string, any> | undefined,
): Record<string, { value: any; filter: FilterType }> => {
  if (!values) {
    return {};
  }

  return Object.keys(values).reduce((a, k) => {
    if (!values[k]) {
      return a;
    }

    const filter = filters.find((f) => f.field === k);

    if (!filter) {
      return a;
    }

    return {
      ...a,
      [k]: {
        value: typeof values[k] === 'string' ? values[k].trim() : values[k],
        filter,
      },
    };
  }, {});
};

const rehydratePersistedFilters = (
  filters: FilterType[],
  persisted: Record<string, { value: any; filter?: FilterType }> | undefined,
): Record<string, { value: any; filter: FilterType }> => {
  if (!persisted) {
    return {};
  }

  return Object.keys(persisted).reduce((a, k) => {
    const filter = filters.find((f) => f.field === k);

    if (!filter) {
      return a;
    }

    return {
      ...a,
      [k]: { ...persisted[k], filter },
    };
  }, {});
};

const getFiltersStructure = (
  filters: Record<string, any>,
): Record<string, any> => {
  return Object.entries(filters).map(
    ([, value]: [string, any]) => value.filter,
  );
};

const checkHasChanges = (
  tableKey: string,
  allOpenFilters: Record<string, any> | null,
  allTableFilters: Record<string, any> | null,
): boolean => {
  if (!allTableFilters || !allOpenFilters) return false;

  const currentTableOpenFilters = allOpenFilters[tableKey];
  if (!currentTableOpenFilters) return false;

  // Get the structure of open filters
  const openFiltersStructure = getFiltersStructure(currentTableOpenFilters);

  return openFiltersStructure.some((openFilter: any) => {
    return !allTableFilters.find((tableFilter: any) => {
      return Object.keys(openFilter || {}).every((key) => {
        if (key === 'options') {
          const openOptions = openFilter[key] || [];
          const tableOptions = tableFilter?.[key] || [];

          if (!tableFilter?.[key]) {
            return true;
          }

          if (openOptions.length !== tableOptions.length) {
            return false;
          }

          const allOpenOptionsMatch = openOptions.every((openOption: any) =>
            tableOptions.some(
              (tableOption: any) =>
                JSON.stringify(openOption) === JSON.stringify(tableOption),
            ),
          );
          return allOpenOptionsMatch;
        }

        return (
          JSON.stringify(openFilter[key]) === JSON.stringify(tableFilter?.[key])
        );
      });
    });
  });
};

const getAllFiltersWithChanges = (
  tableKey: string,
  allOpenFilters: Record<string, any> | null,
  allTableFilters: Record<string, any> | null,
): Record<string, any> | null => {
  if (!allTableFilters) return allOpenFilters;

  const currentTableOpenFilters = allOpenFilters?.[tableKey];

  if (!currentTableOpenFilters) return allOpenFilters;

  const hasChanges = checkHasChanges(tableKey, allOpenFilters, allTableFilters);

  if (hasChanges) {
    const transformedFilters = { ...allOpenFilters };
    delete transformedFilters[tableKey];
    return transformedFilters;
  }

  return allOpenFilters;
};

export interface DataTableFiltersHandle {
  setFilterValue: (field: string, value: any) => void;
  removeFilter: (field: string) => void;
  getFilterValue: (field: string) => any;
}

export const DataTableFilters = React.forwardRef<
  DataTableFiltersHandle,
  FiltersProps
>(
  (
    {
      filters,
      onFilter,
      tableName,
      defaultValues,
      forcedFilter,
      setOpenedFilters,
      doNotUseInContainer = false,
      ...rest
    },
    ref,
  ) => {
    const allOpenFiltersState = useAppSelector(selectOpenFilters);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const user = useAppSelector(selectCurrentUser);
    const { t } = useTranslation();

    const allOpenFilters = useMemo(() => {
      if (!tableName) {
        return allOpenFiltersState;
      }

      return getAllFiltersWithChanges(
        `${TableNamesKeys[tableName]}-${currentAccount?.id}-${user?.id}`,
        allOpenFiltersState,
        filters,
      );
    }, [allOpenFiltersState, filters, tableName, currentAccount?.id, user?.id]);

    const [values, dispatch] = useReducer(
      filtersReducer,
      !forcedFilter
        ? {
            ...buildFiltersFromValues(filters, defaultValues),
            ...(tableName && allOpenFilters
              ? rehydratePersistedFilters(
                  filters,
                  allOpenFilters[
                    `${TableNamesKeys[tableName]}-${currentAccount?.id}-${user?.id}`
                  ],
                )
              : {}),
          }
        : buildFiltersFromValues(filters, forcedFilter),
    );

    useImperativeHandle(ref, () => ({
      setFilterValue: (field: string, value: any) => {
        const filter = filters.find((f) => f.field === field);
        if (!filter) return;
        if (!values[field]) {
          dispatch({ type: 'add', filter, key: field });
        }
        dispatch({ type: 'updateValue', value, key: field });
      },
      removeFilter: (field: string) => {
        if (values[field]) {
          dispatch({ type: 'remove', key: field });
        }
      },
      getFilterValue: (field: string) => {
        return values[field]?.value ?? null;
      },
    }));

    const appDispatcher = useAppDispatch();

    const menuRef = useRef<Menu>(null);
    const items: MenuItem[] = useMemo(
      () =>
        filters.map(
          (f): MenuItem => ({
            label: f.label,
            disabled: !!values[f.field],
            command: () => dispatch({ type: 'add', filter: f, key: f.field }),
          }),
        ),
      [JSON.stringify(filters), JSON.stringify(values)],
    );

    const onChange = useCallback((value: any, field: string) => {
      dispatch({
        type: 'updateValue',
        value,
        key: field,
      });
    }, []);

    const onRemove = useCallback((field: string) => {
      dispatch({
        type: 'remove',
        key: field,
      });
    }, []);

    const getFilterValue = (value: any, filter: FilterType) => {
      if (
        [
          FilterNamesEnum.GROUPS,
          FilterNamesEnum.BRANCH,
          FilterNamesEnum.USER_BRANCH,
          FilterNamesEnum.USER_GROUPS,
          FilterNamesEnum.AWARENESS_REPORTS_BRANCH,
        ].includes(filter.field)
      ) {
        if (filter.type === FilterTypeEnum.SELECT) {
          return value?.id;
        }

        if (filter.type === FilterTypeEnum.MULTI_SELECT) {
          return value?.map((v: any) => v.id);
        }
      }

      if (
        [
          FilterNamesEnum.DEPARTMENT,
          FilterNamesEnum.MANAGER,
          FilterNamesEnum.COUNTRY,
          FilterNamesEnum.JOB,
          FilterNamesEnum.COMPANY_NAME,
          FilterNamesEnum.OFFICE_LOCATION,
          FilterNamesEnum.USER_DEPARTMENT,
          FilterNamesEnum.USER_MANAGER,
          FilterNamesEnum.USER_COUNTRY,
          FilterNamesEnum.USER_JOB,
          FilterNamesEnum.USER_COMPANY_NAME,
          FilterNamesEnum.USER_OFFICE_LOCATION,
        ].includes(filter.field)
      ) {
        if (filter.type === FilterTypeEnum.SELECT) {
          return value?.name;
        }

        if (filter.type === FilterTypeEnum.MULTI_SELECT) {
          return value?.map((v: any) => v.name);
        }
      }

      if (
        [
          FilterNamesEnum.MANAGER_EMAIL,
          FilterNamesEnum.USER_MANAGER_EMAIL,
        ].includes(filter.field)
      ) {
        if (filter.type === FilterTypeEnum.SELECT) {
          return value?.email;
        }

        if (filter.type === FilterTypeEnum.MULTI_SELECT) {
          return value?.map((v: any) => v.email);
        }
      }

      if ([FilterNamesEnum.COURSE_TAGS].includes(filter.field)) {
        return value?.map((v: any) => v.id);
      }

      if ([FilterNamesEnum.EMAIL_TEMPLATE_BY_LANGUAGE].includes(filter.field)) {
        return value?.map((v: any) => v.id).filter(Boolean);
      }

      return value;
    };

    useEffect(() => {
      if (tableName && !forcedFilter && allOpenFilters) {
        const tableKey = `${TableNamesKeys[tableName]}-${currentAccount?.id}-${user?.id}`;

        if (Object.keys(values).length > 0) {
          const hasChanges = checkHasChanges(
            tableKey,
            allOpenFiltersState,
            filters,
          );

          if (hasChanges) {
            const defaultFilters = buildFiltersFromValues(
              filters,
              defaultValues,
            );
            dispatch({
              type: 'clear',
              key: '',
              defaultValues: defaultFilters,
            });
          }
        }
      }
    }, [allOpenFilters, tableName, filters]);

    useEffect(() => {
      onFilter(
        Object.values(values).reduce(
          (a, v) => ({
            ...a,
            [v.filter.field]: getFilterValue(v.value, v.filter),
          }),
          {},
        ),
      );
      if (setOpenedFilters) {
        setOpenedFilters(Object.keys(Object(values)));
      }

      if (tableName && !forcedFilter) {
        appDispatcher(
          setOpenFilters({
            ...allOpenFilters,
            [`${TableNamesKeys[tableName]}-${currentAccount?.id}-${user?.id}`]:
              values,
          }),
        );
      }
    }, [JSON.stringify(values), JSON.stringify(allOpenFilters)]);

    const content = (
      <>
        <Menu model={items} popup ref={menuRef} />
        <AppButton
          className="w-max"
          label={t('filter.add')}
          icon="pi pi-filter"
          type="outlined"
          onClick={(event: SyntheticEvent<HTMLButtonElement>) =>
            menuRef.current?.toggle(event)
          }
        />
        {Object.keys(values).length ? (
          <>
            {Object.keys(values).map((f: string) => (
              <DataTableFilter
                {...values[f]}
                onChange={onChange}
                key={f}
                onRemove={onRemove}
              />
            ))}
          </>
        ) : (
          <></>
        )}
      </>
    );

    return (
      <>
        {doNotUseInContainer ? (
          content
        ) : (
          <FlexContainer
            width="auto"
            flex="1"
            wrap="wrap"
            gap={12}
            justify="flex-start"
            align="flex-start"
            {...rest}
          >
            {content}
          </FlexContainer>
        )}
      </>
    );
  },
);
